import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { formatLogTimestamp } from '../utils/dateUtils';

function SchedulerManagement({ token, timezone }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [pagination, setPagination] = useState({ hasMore: false, nextCursor: null });
  const [stats, setStats] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);

  // Load scheduler configuration
  useEffect(() => {
    fetchConfig();
    fetchStats();
  }, []);

  // Load logs on mount
  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await api.get('/scheduler/config');
      setConfig(response.data);
    } catch (error) {
      console.error('Error fetching scheduler config:', error);
      alert('Failed to load scheduler configuration');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/scheduler/logs/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching scheduler stats:', error);
    }
  };

  const fetchLogs = async (cursor = null) => {
    setLogsLoading(true);
    try {
      const params = { limit: 20 };
      if (cursor) params.cursor = cursor;

      const response = await api.get('/scheduler/logs', { params });

      if (cursor) {
        // Append to existing logs for "Load More"
        setLogs(prev => [...prev, ...response.data.logs]);
      } else {
        // Replace logs for initial load or refresh
        setLogs(response.data.logs);
      }

      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleEnableToggle = async () => {
    setSaving(true);
    try {
      await api.put('/scheduler/config', {
        enabled: !config.enabled,
        cron_schedule: config.cron_schedule
      });
      await fetchConfig();
      alert(`Scheduler ${!config.enabled ? 'enabled' : 'disabled'} successfully`);
    } catch (error) {
      console.error('Error toggling scheduler:', error);
      alert('Failed to update scheduler');
    } finally {
      setSaving(false);
    }
  };

  const handleScheduleUpdate = async () => {
    const newSchedule = prompt('Enter cron schedule (e.g., "0 9 * * *" for daily at 9 AM):', config.cron_schedule);
    if (!newSchedule) return;

    setSaving(true);
    try {
      await api.put('/scheduler/config', {
        enabled: config.enabled,
        cron_schedule: newSchedule
      });
      await fetchConfig();
      alert('Schedule updated successfully');
    } catch (error) {
      console.error('Error updating schedule:', error);
      alert(error.response?.data?.error || 'Failed to update schedule');
    } finally {
      setSaving(false);
    }
  };

  const handleManualTrigger = async () => {
    if (!window.confirm('Trigger reminder job manually? This will send reminders to all eligible users.')) {
      return;
    }

    try {
      await api.post('/scheduler/trigger', {});
      alert('Scheduler triggered successfully. Check logs for results.');
      // Refresh logs after a delay to show new entry
      setTimeout(() => {
        fetchLogs();
        fetchStats();
      }, 2000);
    } catch (error) {
      console.error('Error triggering scheduler:', error);
      alert('Failed to trigger scheduler');
    }
  };

  const handleClearOldLogs = async () => {
    if (!window.confirm('Clear old logs? This will keep only the 100 most recent logs.')) {
      return;
    }

    try {
      const response = await api.delete('/scheduler/logs');
      alert(`${response.data.deleted_count} old logs cleared`);
      fetchLogs();
      fetchStats();
    } catch (error) {
      console.error('Error clearing logs:', error);
      alert('Failed to clear logs');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatLogTimestamp(dateString, timezone);
  };

  const formatDuration = (startedAt, completedAt) => {
    if (!startedAt || !completedAt) return 'N/A';
    const duration = (new Date(completedAt) - new Date(startedAt)) / 1000;
    return `${duration.toFixed(1)}s`;
  };

  if (loading) {
    return <div style={{ padding: '2rem' }}>Loading scheduler configuration...</div>;
  }

  if (!config) {
    return <div style={{ padding: '2rem' }}>Failed to load scheduler configuration</div>;
  }

  return (
    <div className="admin-section">
      <h2>Reminder Scheduler</h2>

      {/* Configuration Section */}
      <div style={{
        background: 'var(--color-surface)',
        padding: '1.5rem',
        borderRadius: '0.5rem',
        marginBottom: '2rem'
      }}>
        <h3 style={{ marginTop: 0 }}>Configuration</h3>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <strong>Status:</strong>{' '}
            <span style={{ color: config.enabled ? 'var(--color-success)' : 'var(--color-text-secondary)' }}>
              {config.enabled ? '✅ Enabled' : '⏸️ Disabled'}
            </span>
          </div>
          <div>
            <strong>Schedule:</strong> <code>{config.cron_schedule}</code>
          </div>
          <div>
            <strong>Running:</strong> {config.is_running ? '🟢 Yes' : '⚪ No'}
          </div>
          <div>
            <strong>Last Run:</strong> {formatDate(config.last_run_at)}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            onClick={handleEnableToggle}
            disabled={saving}
            style={{
              background: config.enabled ? 'var(--color-warning)' : 'var(--color-success)',
              color: 'white',
              borderRadius: '9999px',
              padding: '0.625rem 1.25rem'
            }}
          >
            {config.enabled ? 'Disable Scheduler' : 'Enable Scheduler'}
          </button>
          <button onClick={handleScheduleUpdate} disabled={saving} style={{ borderRadius: '9999px', padding: '0.625rem 1.25rem' }}>
            Change Schedule
          </button>
          <button onClick={handleManualTrigger} disabled={!config.enabled} style={{
            background: 'var(--color-primary)',
            color: 'white',
            borderRadius: '9999px',
            padding: '0.625rem 1.25rem'
          }}>
            Trigger Now (Test)
          </button>
        </div>

        <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
          <strong>Cron Format:</strong> minute hour day month weekday
          <br />
          Examples: <code>0 9 * * *</code> (daily at 9 AM), <code>0 9 * * 1</code> (Mondays at 9 AM)
        </div>
      </div>

      {/* Statistics Section */}
      {stats && (
        <div style={{
          background: 'var(--color-surface)',
          padding: '1.5rem',
          borderRadius: '0.5rem',
          marginBottom: '2rem'
        }}>
          <h3 style={{ marginTop: 0 }}>Statistics</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
            <div>
              <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>Total Runs</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{stats.total_runs || 0}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>Successful</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--color-success)' }}>
                {stats.successful_runs || 0}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>Failed</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--color-danger)' }}>
                {stats.failed_runs || 0}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>Total Emails</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{stats.total_emails_sent || 0}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>Total SMS</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{stats.total_sms_sent || 0}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>Users Processed</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{stats.total_users_processed || 0}</div>
            </div>
          </div>
        </div>
      )}

      {/* Execution Logs Section */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0 }}>Execution Logs</h3>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={() => fetchLogs()} style={{ borderRadius: '9999px', padding: '0.625rem 1.25rem' }}>Refresh</button>
            <button onClick={handleClearOldLogs} style={{ background: 'var(--color-warning)', color: 'white', borderRadius: '9999px', padding: '0.625rem 1.25rem' }}>
              Clear Old Logs
            </button>
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Started At</th>
                <th>Completed At</th>
                <th>Duration</th>
                <th>Status</th>
                <th>Users</th>
                <th>Emails</th>
                <th>SMS</th>
                <th>Errors</th>
                <th>Error Message</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-secondary)' }}>
                    No logs yet
                  </td>
                </tr>
              ) : (
                logs.map(log => (
                  <tr
                    key={log.id}
                    onClick={() => setSelectedLog(log)}
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-surface)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
                  >
                    <td>{log.id}</td>
                    <td>{formatDate(log.run_started_at)}</td>
                    <td>{formatDate(log.run_completed_at)}</td>
                    <td>{formatDuration(log.run_started_at, log.run_completed_at)}</td>
                    <td>
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        background: log.status === 'completed'
                          ? 'var(--color-success)'
                          : log.status === 'failed'
                          ? 'var(--color-danger)'
                          : 'var(--color-warning)',
                        color: 'white'
                      }}>
                        {log.status}
                      </span>
                    </td>
                    <td>{log.users_processed || 0}</td>
                    <td>{log.emails_sent || 0}</td>
                    <td>{log.sms_sent || 0}</td>
                    <td>{log.errors_count || 0}</td>
                    <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {log.error_message || '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination.hasMore && (
          <div style={{ textAlign: 'center', marginTop: '1rem' }}>
            <button
              onClick={() => fetchLogs(pagination.nextCursor)}
              disabled={logsLoading}
              style={{ borderRadius: '9999px', padding: '0.625rem 1.25rem' }}
            >
              {logsLoading ? 'Loading...' : 'Load More'}
            </button>
            <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
              Showing {logs.length} of {pagination.total} logs
            </div>
          </div>
        )}
      </div>

      {/* Log Details Modal */}
      {selectedLog && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setSelectedLog(null)}
        >
          <div
            style={{
              background: 'var(--color-card-bg)',
              border: '1px solid var(--color-border)',
              borderRadius: '0.5rem',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
              padding: '2rem',
              position: 'relative',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedLog(null)}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: 'none',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                color: 'var(--color-text)',
                padding: '0.25rem',
                lineHeight: 1
              }}
            >
              ×
            </button>

            <h3 style={{ marginTop: 0, marginBottom: '1.5rem', color: 'var(--color-heading)' }}>
              Scheduler Log Details
            </h3>

            <div style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <strong style={{ display: 'block', marginBottom: '0.25rem', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                  Log ID
                </strong>
                <div>{selectedLog.id}</div>
              </div>

              <div>
                <strong style={{ display: 'block', marginBottom: '0.25rem', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                  Status
                </strong>
                <span style={{
                  padding: '0.25rem 0.5rem',
                  borderRadius: '0.25rem',
                  fontSize: '0.875rem',
                  fontWeight: 'bold',
                  background: selectedLog.status === 'completed'
                    ? 'var(--color-success)'
                    : selectedLog.status === 'failed'
                    ? 'var(--color-danger)'
                    : 'var(--color-warning)',
                  color: 'white'
                }}>
                  {selectedLog.status}
                </span>
              </div>

              <div>
                <strong style={{ display: 'block', marginBottom: '0.25rem', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                  Started At
                </strong>
                <div>{formatDate(selectedLog.run_started_at)}</div>
              </div>

              <div>
                <strong style={{ display: 'block', marginBottom: '0.25rem', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                  Completed At
                </strong>
                <div>{formatDate(selectedLog.run_completed_at)}</div>
              </div>

              <div>
                <strong style={{ display: 'block', marginBottom: '0.25rem', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                  Duration
                </strong>
                <div>{formatDuration(selectedLog.run_started_at, selectedLog.run_completed_at)}</div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', padding: '1rem', background: 'var(--color-surface)', borderRadius: '0.5rem' }}>
                <div>
                  <strong style={{ display: 'block', marginBottom: '0.25rem', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                    Users Processed
                  </strong>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{selectedLog.users_processed || 0}</div>
                </div>
                <div>
                  <strong style={{ display: 'block', marginBottom: '0.25rem', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                    Emails Sent
                  </strong>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--color-primary)' }}>{selectedLog.emails_sent || 0}</div>
                </div>
                <div>
                  <strong style={{ display: 'block', marginBottom: '0.25rem', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                    SMS Sent
                  </strong>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--color-primary)' }}>{selectedLog.sms_sent || 0}</div>
                </div>
                <div>
                  <strong style={{ display: 'block', marginBottom: '0.25rem', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                    Errors
                  </strong>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: selectedLog.errors_count > 0 ? 'var(--color-danger)' : 'var(--color-text)' }}>
                    {selectedLog.errors_count || 0}
                  </div>
                </div>
              </div>

              {selectedLog.error_message && (
                <div>
                  <strong style={{ display: 'block', marginBottom: '0.25rem', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                    Error Message
                  </strong>
                  <div style={{
                    padding: '0.75rem',
                    background: 'var(--color-danger)',
                    color: 'white',
                    borderRadius: '0.25rem',
                    fontSize: '0.875rem',
                    wordBreak: 'break-word'
                  }}>
                    {selectedLog.error_message}
                  </div>
                </div>
              )}

              {selectedLog.details && (
                <div>
                  <strong style={{ display: 'block', marginBottom: '0.25rem', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                    Additional Details
                  </strong>
                  <pre style={{
                    padding: '0.75rem',
                    background: 'var(--color-surface)',
                    borderRadius: '0.25rem',
                    fontSize: '0.75rem',
                    overflow: 'auto',
                    maxHeight: '200px'
                  }}>
                    {JSON.stringify(selectedLog.details, null, 2)}
                  </pre>
                </div>
              )}

              <div>
                <strong style={{ display: 'block', marginBottom: '0.25rem', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                  Created At
                </strong>
                <div style={{ fontSize: '0.875rem' }}>{formatDate(selectedLog.created_at)}</div>
              </div>
            </div>

            <div style={{ marginTop: '2rem', textAlign: 'right' }}>
              <button onClick={() => setSelectedLog(null)} style={{ borderRadius: '9999px', padding: '0.625rem 1.25rem' }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SchedulerManagement;
